/**
 * 🔐 LLAMADAS AUTENTICADAS AL BACKEND (`IGNIA-backend`)
 *
 * Los endpoints de subida (`/api/upload-*`) y de correo (`/api/correo/*`) ya no
 * son públicos: exigen un ID token de Firebase en
 * `Authorization: Bearer <idToken>`, que el servidor verifica con el Admin SDK
 * (`server/middleware/autenticacion.js`). Sin eso, cualquiera que conociese la
 * URL podía llenar el almacenamiento de la empresa o usar el servidor como
 * relé de correo con el remitente de agpsoluciones.com (§5.2).
 *
 * `getIdToken()` devuelve el token de la caché y lo renueva solo cuando le
 * quedan menos de 5 minutos, así que llamarlo en cada petición no cuesta una
 * ida y vuelta a Firebase.
 */

import { auth } from '../config/firebase';

/** Se lanza cuando no hay sesión: quien llama debe mandar al usuario al login. */
export class SinSesionError extends Error {
  constructor() {
    super('La sesión ha caducado. Vuelve a iniciar sesión.');
    this.name = 'SinSesionError';
  }
}

/**
 * Cabeceras con el ID token de la sesión actual.
 *
 * @param {object} [extra] cabeceras adicionales (Content-Type, etc.)
 * @throws {SinSesionError} si no hay usuario autenticado
 */
export async function cabecerasAutenticadas(extra = {}) {
  const usuario = auth.currentUser;
  if (!usuario) throw new SinSesionError();

  const token = await usuario.getIdToken();
  return { ...extra, Authorization: `Bearer ${token}` };
}

/**
 * `fetch` con el ID token puesto. Mantiene la firma de `fetch`, así que sirve
 * como reemplazo directo en las llamadas al backend.
 */
export async function fetchAutenticado(url, opciones = {}) {
  const headers = await cabecerasAutenticadas(opciones.headers || {});
  return fetch(url, { ...opciones, headers });
}

/**
 * ⏳ REINTENTOS: EL SERVIDOR DORMIDO NO ES UN ERROR
 *
 * El backend escala a cero (Cloud Run), así que la primera llamada después de
 * un rato puede morir de tres formas que no dicen nada del panel ni de los
 * datos: la conexión se corta mientras arranca el contenedor (`fetch` lanza),
 * o el propio Cloud Run responde 502/503/504 porque todavía no hay instancia
 * que atienda. Antes cualquiera de las tres dejaba al administrador con un
 * "no se pudo generar el documento" y a la segunda pulsación funcionaba.
 *
 * `utils/precalentarBackend.js` intenta que no llegue a pasar; esto es la red
 * por debajo, para el redespliegue y para el pico que arranca una instancia
 * nueva. Los reintentos son pocos y cortos a propósito: un backend realmente
 * caído tiene que decirse, no disimularse.
 */

/** Respuestas que significan "todavía no hay quien atienda", no "esto está mal". */
export const ESTADOS_REINTENTABLES = [502, 503, 504];

/** Esperas entre intentos. Su longitud es el número de reintentos. */
export const ESPERAS_REINTENTO_MS = [1000, 3000];

const dormir = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * `fetchAutenticado` que reintenta mientras parezca un arranque en frío.
 *
 * @param {string} url
 * @param {object} [opciones] las de `fetch`
 * @param {object} [config]
 * @param {number[]} [config.esperas] esperas entre intentos (ms)
 * @param {(respuesta: Response) => boolean|Promise<boolean>} [config.sePuedeReintentar]
 *        decide si una respuesta merece otro intento. Por defecto, su código.
 *        ⚠️ Si necesita el cuerpo, tiene que leerlo de `respuesta.clone()`: el
 *        original se devuelve intacto a quien llamó.
 * @param {(aviso: {intento: number, total: number, motivo: string}) => void} [config.alReintentar]
 *        se avisa ANTES de esperar, para poder contárselo al usuario.
 */
export async function fetchConReintento(url, opciones = {}, config = {}) {
  const {
    esperas = ESPERAS_REINTENTO_MS,
    sePuedeReintentar = (respuesta) => ESTADOS_REINTENTABLES.includes(respuesta.status),
    alReintentar
  } = config;

  const avisar = (intento, motivo) => {
    if (alReintentar) alReintentar({ intento, total: esperas.length, motivo });
  };

  for (let intento = 0; ; intento++) {
    const quedanIntentos = intento < esperas.length;

    let respuesta;
    try {
      respuesta = await fetchAutenticado(url, opciones);
    } catch (error) {
      // Sin sesión no se arregla esperando: hay que volver a entrar
      if (error instanceof SinSesionError || !quedanIntentos) throw error;
      avisar(intento + 1, error.message);
      await dormir(esperas[intento]);
      continue;
    }

    if (!quedanIntentos || !(await sePuedeReintentar(respuesta))) return respuesta;

    avisar(intento + 1, `el servidor respondió ${respuesta.status}`);
    await dormir(esperas[intento]);
  }
}
